import { Icon } from "@blueprintjs/core";
import { ReactComponent as BatteryStorageIcon } from "in_queue/icons/fuelType/battery-storage.svg";
import { ReactComponent as GeneratorIcon } from "in_queue/icons/fuelType/generator.svg";
import { ReactComponent as SolarIcon } from "in_queue/icons/fuelType/solar.svg";
import React from "react";
import { RegionType } from "./regionType";

export const SOLAR_TYPE = "solar";
export const WIND_TYPE = "wind";
export const STORAGE_TYPE = "storage";
export const LOAD_TYPE = "load";

export const GENERATOR_TYPES = [
    SOLAR_TYPE,
    WIND_TYPE,
    STORAGE_TYPE,
    LOAD_TYPE
] as const;
export type GeneratorType = (typeof GENERATOR_TYPES)[number];

export const isKnownGeneratorType = (type: string): type is GeneratorType => {
    return GENERATOR_TYPES.includes(type as GeneratorType);
};

export const convertToGeneratorType = (string: string): GeneratorType => {
    if (isKnownGeneratorType(string)) {
        return string;
    }

    throw new Error("Unexpected generator type");
};

const REGION_TO_GENERATOR_TYPE_TO_OVERRIDDEN_LABEL: {
    [key in RegionType]?: { [key in GeneratorType]?: string };
} = {
    WECC: {
        solar: "Solar / Wind",
        storage: "Storage / Hybrid"
    },
    MISO: {
        storage: "Standalone Storage"
    },
    TVA: { solar: "Solar / Wind", storage: "Standalone Storage" },
    SOCO: { solar: "Solar / Wind" },
    DUKE: { storage: "Storage" },
    PJM: { storage: "Standalone Storage" },
    CAISO: { storage: "Storage" }
};

/**
 * SVG icon components for the generator types that use the in-queue
 * fuel-type SVGs. Wind is intentionally NOT in this map — we use the
 * Blueprint `wind` glyph for that type since the SVG version reads
 * poorly. `GeneratorTypeIcon` below routes the wind case to Blueprint
 * and everything else to these SVGs.
 *
 * `LOAD_TYPE` has no dedicated fuel-style icon in the in-queue set, so
 * it falls back to the generic generator badge.
 */
const GENERATOR_TYPE_TO_SVG_ICON: Record<
    Exclude<GeneratorType, "wind">,
    React.FC<React.SVGProps<SVGSVGElement>>
> = {
    [SOLAR_TYPE]: SolarIcon,
    [STORAGE_TYPE]: BatteryStorageIcon,
    [LOAD_TYPE]: GeneratorIcon
};

/**
 * Canonical fuel-type colors copied from the in-queue `FuelTypeIcon`
 * palette. Kept in sync manually so the prospecting bus page reads as
 * "same icon, same color" as the in-queue app.
 */
export const GENERATOR_TYPE_TO_ICON_COLOR: Record<GeneratorType, string> = {
    [SOLAR_TYPE]: "#f59e0b", // amber
    [WIND_TYPE]: "#3b82f6", // blue
    [STORAGE_TYPE]: "#10b981", // green
    [LOAD_TYPE]: "#4b5563" // gray (generic generator)
};

/**
 * Renders the canonical icon for a generator type. Wind uses
 * Blueprint's built-in `wind` glyph; everything else uses the in-queue
 * fuel-type SVGs (color-tinted via `currentColor`). Designed to drop
 * straight into a Blueprint `Tag`'s `icon` prop.
 */
export const GeneratorTypeIcon: React.FC<{
    readonly type: GeneratorType;
    readonly size?: number;
}> = ({ type, size = 14 }) => {
    const color = GENERATOR_TYPE_TO_ICON_COLOR[type];
    if (type === WIND_TYPE) {
        // Blueprint's `wind` glyph fills its bounding box more
        // aggressively than the in-queue SVGs, so render it ~2px
        // smaller to keep the visual weight consistent with the
        // other generator-type icons in the same row.
        return <Icon icon="wind" size={size - 2} color={color} />;
    }
    const SvgIcon = GENERATOR_TYPE_TO_SVG_ICON[type];
    return <SvgIcon width={size} height={size} style={{ color }} />;
};

export const GENERATOR_TYPE_TO_DEFAULT_LABEL: {
    [key in GeneratorType]: string;
} = {
    solar: "Solar",
    wind: "Wind",
    storage: "Storage / Solar Hybrid",
    load: "Large Load"
};

export const getLabelForRegionAndGeneratorType = (
    region: RegionType,
    generatorType: GeneratorType
): string => {
    const maybeOverride =
        REGION_TO_GENERATOR_TYPE_TO_OVERRIDDEN_LABEL[region]?.[generatorType];
    return maybeOverride
        ? maybeOverride
        : GENERATOR_TYPE_TO_DEFAULT_LABEL[generatorType];
};
